import { Link } from "react-router";
import { ExternalLink, Send, TriangleAlert, X } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Skeleton, Switch, Tooltip, cn } from "@ncp/ui";
import { useConnectorCatalog } from "./api";

const strip = (grant: string) => grant.trim().replace(/^connector\./i, "");

/**
 * Which connector operations an agent or assistant may call, as `{connector}.{operation}` or `{connector}.*` grants.
 * Agents only ever read: operations that change data are offered to assistants alone, where each call needs the user's confirmation.
 */
export function ConnectorGrantPicker({ value, onChange, readOnly, readsOnly = false }: { value: string[]; onChange: (next: string[]) => void; readOnly: boolean; readsOnly?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const catalog = useConnectorCatalog();
  const grants = value.map(strip);
  const has = (g: string) => grants.some((x) => x.toLowerCase() === g.toLowerCase());
  const known = new Set((catalog.data ?? []).flatMap((c) => [`${c.key}.*`, ...c.operations.map((o) => `${c.key}.${o.key}`)].map((x) => x.toLowerCase())));
  const unknown = catalog.data ? grants.filter((g) => g !== "*" && !known.has(g.toLowerCase())) : [];
  const set = (next: string[]) => onChange([...new Set(next)]);

  if (catalog.isLoading) return <Skeleton className="h-24" />;
  if ((catalog.data ?? []).length === 0) return <p className="rounded-xl bg-sand-50 p-3 text-xs text-muted-foreground">{t("admin:connectors.grants.none")} <Link to="/integrations/connectors" className="text-aegold-800 hover:underline">{t("admin:connectors.grants.build")}</Link></p>;
  return (
    <div className="space-y-2">
      {has("*") ? <p className="rounded-lg bg-warning-50 px-2 py-1 text-[11px] text-warning-800">{t("admin:connectors.grants.everything")}</p> : null}
      {(catalog.data ?? []).map((c) => {
        const all = has(`${c.key}.*`) || has("*");
        const mine = grants.filter((g) => g.toLowerCase().startsWith(`${c.key.toLowerCase()}.`));
        return (
          <div key={c.key} className={cn("rounded-xl border p-2.5", mine.length || all ? "border-aegold-200 bg-aegold-50/30" : "border-sand-200")}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{locale === "ar" && c.nameAr ? c.nameAr : c.name}</p>
                <p className="truncate font-mono text-[10px] text-muted-foreground" dir="ltr">{c.key}</p>
              </div>
              <Link to={`/integrations/connectors/${encodeURIComponent(c.key)}`} className="text-muted-foreground hover:text-aegold-800" aria-label={t("admin:connectors.grants.open")}><ExternalLink className="size-3.5" /></Link>
              <label className="flex items-center gap-1.5 text-[11px]">
                <Switch checked={all} disabled={readOnly || has("*")} onCheckedChange={(v) => set(v ? [...grants.filter((g) => !mine.includes(g)), `${c.key}.*`] : grants.filter((g) => g.toLowerCase() !== `${c.key}.*`.toLowerCase()))} />
                {t("admin:connectors.grants.all")}
              </label>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {c.operations.map((o) => {
                const grant = `${c.key}.${o.key}`;
                const on = all || has(grant);
                const blocked = readsOnly && !o.readOnly;
                return (
                  <Tooltip key={o.key} content={blocked ? t("admin:connectors.grants.agentsRead") : o.description ?? o.name}>
                    <button type="button" disabled={readOnly || all || blocked}
                      onClick={() => set(on ? grants.filter((g) => g.toLowerCase() !== grant.toLowerCase()) : [...grants, grant])}
                      className={cn("flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] transition-colors disabled:cursor-default",
                        on && !blocked ? "border-aegold-400 bg-aegold-50 text-aeblack-900" : "border-sand-200 bg-white text-muted-foreground hover:border-aegold-300",
                        blocked && "opacity-50 line-through")}>
                      <span className="font-mono text-[10px]">{o.method}</span> {locale === "ar" && o.nameAr ? o.nameAr : o.name}
                      {!o.readOnly ? <Send className="size-3 text-warning-600" /> : null}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
            {all && readsOnly && c.operations.some((o) => !o.readOnly) ? <p className="mt-1.5 text-[11px] text-muted-foreground">{t("admin:connectors.grants.allReadsOnly")}</p> : null}
          </div>
        );
      })}
      {unknown.length ? (
        <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-danger-200 bg-danger-50/40 p-2 text-[11px]">
          <TriangleAlert className="size-3.5 text-danger-600" /> <span className="text-danger-800">{t("admin:connectors.grants.unknown")}</span>
          {unknown.map((g) => (
            <Badge key={g} variant="danger" size="sm" className="font-mono">{g}{!readOnly ? <Button size="icon-sm" variant="ghost" className="-me-1 size-4" aria-label={t("admin:common.remove")} onClick={() => set(grants.filter((x) => x !== g))}><X className="size-3" /></Button> : null}</Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
